import { yupResolver } from '@hookform/resolvers/yup';
import { alpha, Autocomplete, Box, Button, Card, CardContent, Chip, Grid, IconButton, Stack, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { Fragment, SyntheticEvent, useEffect, useState } from 'react';
import { useFieldArray, UseFieldArrayReturn, useForm } from 'react-hook-form';
import { useBlocker, useLocation } from 'react-router';
import { InferType } from 'yup';

import { WarningRounded } from '@mui/icons-material';
import {
  EmptyContent,
  enqueueSnackbar,
  fNumber,
  FormProvider,
  getCurrencySymbol,
  RHFAutocomplete,
  RHFDatePicker,
  RHFRadioGroup,
  RHFReactNumberFormat,
  RHFTextField,
  useBoolean,
  useDefaultEnumsStore,
  useDirtyFieldsStore,
  useFactFindConfigStore,
  useFactFindErrorStore,
  useFactFindProcessStore,
  useUserAccessStore,
  useUserDetailsStore,
} from '@zeta-core';
import { PlusIcon } from '@zeta-core/assets';
import {
  AIInteractionService,
  IFactFindProcessSectionType,
  IFactFindProcessStatus,
  InteractionSubSectionType,
  InvestorService,
  IPatchAIInteractionSectionRequest,
} from '@zeta-services';
import {
  FactFindType,
  ICaptureFactFindExpenditureRequest,
  IClientCardItem,
  IFactFindEntity,
  IFFSummaryCalculateRequest,
  IFFSummaryCalculateResponse,
  IViewFactFindExpenditureResponse,
  IViewFactFindIncomeResponse,
  IViewFactFindRequest,
} from '@zeta-types';
import { ConflictsDialog } from '../../../components/conflicts-dialog';
import { getClientFeatureAccess } from '../../../helpers/access-control-helper';
import AllTranscripts from '../all-transcripts';
import { getLabel } from '../common/commons';
import FactFindHeading from '../common/fact-find-heading';
import ConfirmNavigationDialog from '../components/confirmation-dialog';
import ErrorDialog from '../components/error-dialog';
import { ErrorSection } from '../components/error-section';
import SaveDialog from '../components/save-dialog';
import SimpleFieldsTable from '../components/simple-fields-table';
import SuccessDialog from '../components/success-dialog';
import SummaryTabComponent from '../components/summary-tab-component';
import FactFindFooter from '../fact-find-layout/fact-find-footer';
import { useSourceControlContext } from '../fact-find-layout/sourcecontrol-context-component';
import Transcript from '../transcript';
import { createExpenditurePayload, expenditureDefaultValues, expenditureValidationSchema } from './form-helpers';
import { transformExpenditureData } from './transform-expenditure-data';

interface ISummaryItem {
  title: string;
  value: number;
}

type EVSchemaType = InferType<typeof expenditureValidationSchema>;

type tabType = 'client' | 'partner' | 'joint' | 'summary';

export const ExpenditurePage = ({
  isClient,
  isManagedClient,
  from,
  refetch,
  clientData,
  conflictData,
  clientIncomeData,
  clientSummary,
  updatedClientPartnerDetails,
}: {
  from: string;
  isClient?: boolean;
  isManagedClient?: boolean;
  from: FactFindType;
}) => {
  const userAccess = useUserAccessStore((state) => state.userAccess);
  const location = useLocation();
  const { accessibleEntityID } = useUserDetailsStore((state) => state.user);
  let state = (location.state || {}) as { entityData: IFactFindEntity; clientData: IClientCardItem };
  if (isClient) {
    state = { clientData: { clientID: accessibleEntityID } };
  }
  const isTranscript = useBoolean();
  const isAllTranscripts = useBoolean();
  const [currentTab, setCurrentTab] = useState<tabType>('summary');
  const updateDirtyFieldsState = useDirtyFieldsStore((state) => state.updateDirtyFieldsState);
  const [confirmNavigationModal, setConfirmNavigationModal] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);
  const [incomeData, setIncomeData] = useState<IViewFactFindIncomeResponse['response']['income'] | null>(null);
  const [conflictsDialog, setConflictsDialog] = useState<boolean>(false);
  const [totalIncomeMonthly, setTotalIncomeMonthly] = useState<number>(0);
  const [totalIncomeAnnually, setTotalIncomeAnnually] = useState<number>(0);
  const { data: sourceControlData, mutateAsync: sourceControlMutateAsync, clientPartnerDetails } = useSourceControlContext() || {};
  const ffConfig = useFactFindConfigStore((state) => state?.factFindConfigDetails?.individualFactFindSectionConfig?.expenditure);
  const ffErrors = useFactFindErrorStore((state) => state?.ffErrors);
  const updateFFErrorStore = useFactFindErrorStore((state) => state?.updateFFErrorStore);
  const fieldErrors = ffErrors?.find((error) => error.section === InteractionSubSectionType.expenditure_details)?.missing_fields;
  const sectionsProcessDetails = useFactFindProcessStore((state) => state.sections);
  const currentSection = sectionsProcessDetails?.find((section) => section.section_name === IFactFindProcessSectionType.expenditure)?.status;
  const planningStatus = location?.state?.clientData?.planningStatus;
  const readOnly = getClientFeatureAccess(userAccess?.access, 'Advisor-Client-FF', planningStatus).isReadOnly;
  const isReadOnly =
    from === FactFindType.PARA_PLANNER ||
    currentSection === IFactFindProcessStatus.pending ||
    currentSection === IFactFindProcessStatus.started ||
    isClient ||
    readOnly;

  const methods = useForm<EVSchemaType>({
    defaultValues: expenditureDefaultValues(ffConfig),
    resolver: yupResolver(expenditureValidationSchema),
    mode: 'all',
    reValidateMode: 'onChange',
  });
  const { reset } = methods;

  const fieldArrayClient = useFieldArray({ name: 'client', control: methods.control });
  const fieldArrayPartner = useFieldArray({ name: 'partner', control: methods.control });
  const fieldArrayJoint = useFieldArray({ name: 'joint', control: methods.control });

  const isDirtyInPage = methods.formState.isDirty;
  const blocker = useBlocker(({ nextLocation, currentLocation }) => isDirtyInPage && currentLocation.pathname !== nextLocation.pathname);

  const isSuccess = useBoolean(false);
  const isError = useBoolean(false);

  useEffect(() => {
    if (isDirtyInPage) updateDirtyFieldsState(true);
  }, [isDirtyInPage, updateDirtyFieldsState]);

  useEffect(() => {
    if (blocker.state === 'blocked' && showPrompt) setConfirmNavigationModal(true);
    else blocker?.proceed?.();
  }, [blocker, showPrompt]);

  const [summaryData, setSummaryData] = useState({});

  const calculateSummary = useMutation({
    mutationFn: (payload: IFFSummaryCalculateRequest) => InvestorService().ffSummaryCalculate(payload),

    onSuccess: (response: AxiosResponse<IFFSummaryCalculateResponse>) => {
      setSummaryData(response.data.response);
    },
  });

  useEffect(() => {
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      calculateSummary.mutateAsync({ clientID: state.clientData.clientID, sectionType: 'Expenditure' });
      viewExpenditure.mutateAsync({ clientID: state.clientData.clientID, forExternalUse: true });
      viewIncome.mutateAsync({ clientID: state.clientData.clientID, forExternalUse: true });
    }
    if (from === FactFindType.AI) {
      setIncomeData(clientIncomeData?.response?.income);
      setSummaryData(clientSummary);
      if (clientData?.response?.expenditure) reset(transformExpenditureData(clientData.response.expenditure, ffConfig));
    }
  }, [from, state.clientData.clientID, clientData, clientIncomeData, clientSummary, reset, ffConfig]);

  const viewExpenditure = useMutation({
    mutationFn: (payload: IViewFactFindRequest) => InvestorService().viewExpenditure(payload),
    onSuccess: (response: AxiosResponse<IViewFactFindExpenditureResponse>) => {
      if (response.data.response.expenditure) methods.reset(transformExpenditureData(response.data.response.expenditure, ffConfig));
    },
  });

  const viewIncome = useMutation({
    mutationFn: (payload: IViewFactFindRequest) => InvestorService().viewIncome(payload),
    onSuccess: (response: AxiosResponse<IViewFactFindIncomeResponse>) => {
      setIncomeData(response.data.response.income);
    },
  });

  const captureExpenditure = useMutation({
    mutationFn: (payload: ICaptureFactFindExpenditureRequest) => InvestorService().captureExpenditure(payload),
    onSuccess: () => handleMutationSuccess(),
    onError: () => handleMutationError(),
  });

  const captureExpenditureAI = useMutation({
    mutationFn: (payload: IPatchAIInteractionSectionRequest) => AIInteractionService().patchClientData(payload),
    onSuccess: () => {
      handleMutationSuccess();
      updateFFErrorStore({ ffErrors: null });
    },
    onError: () => handleMutationError(),
  });

  const handleMutationSuccess = () => {
    methods.reset({}, { keepValues: true });
    if (from === FactFindType.AI) {
      refetch?.();
    }
    sourceControlMutateAsync({ clientID: state?.clientData?.clientID });
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      viewExpenditure.mutateAsync({ clientID: state.clientData.clientID });
      calculateSummary.mutateAsync({ clientID: state.clientData.clientID, sectionType: 'Expenditure' });
    }
    setShowPrompt(false);
    enqueueSnackbar('Expenditure details saved successfully', { variant: 'success' });
  };

  const handleMutationError = () => {
    isError.onTrue();
    setTimeout(() => isError.onFalse(), 3000);
  };

  const calculateTotalIncome = (incomeData: IViewFactFindIncomeResponse['response']['income']) => {
    let totalAnnual = 0;
    let totalMonthly = 0;
    const calculateIncome = (amount: string | number, frequency: string) => {
      if (frequency?.toLowerCase() === 'annually') totalAnnual += Number(amount);
      else {
        totalAnnual += Number(amount) * 12;
        totalMonthly += Number(amount);
      }
    };

    const getType = () => {
      if (currentTab === 'client') return 'clientIncome';
      if (currentTab === 'partner') return 'partnerIncome';
      if (currentTab === 'joint') return 'jointIncome';
      return 'clientIncome';
    };

    type IncomeType = 'clientIncome' | 'partnerIncome' | 'jointIncome';

    const data = incomeData[getType() as IncomeType];
    data?.forEach((item) => calculateIncome(item.amount, item.frequency));

    return { annual: totalAnnual, monthly: totalAnnual / 12 };
  };

  useEffect(() => {
    if (incomeData) {
      const income = calculateTotalIncome(incomeData);

      setTotalIncomeAnnually(income.annual);
      setTotalIncomeMonthly(income.monthly);
    }
  }, [currentTab, incomeData]);

  const handleChangeTab = (_event: SyntheticEvent, value: tabType) => setCurrentTab(value);

  const calculateTotalExpenditure = (field: 'Monthly' | 'Annually') => {
    const values = methods.watch(currentTab);
    return (
      values?.reduce((total: number, item: any) => {
        const amount = parseFloat(item.amount) || 0;
        if (field === 'Monthly' && item.frequency === 'Monthly') {
          return total + amount;
        } else if (field === 'Annually' && item.frequency) {
          return total + (item.frequency === 'Monthly' ? amount * 12 : amount);
        }
        return total;
      }, 0) || 0
    );
  };

  const handleDiscardAndProceed = () => {
    setConfirmNavigationModal(false);
    blocker?.proceed?.();
    setShowPrompt(false);
    updateDirtyFieldsState(false);
  };

  const handleCloseNavigationModal = () => {
    setConfirmNavigationModal(false);
    blocker?.reset?.();
    setShowPrompt(true);
  };

  const fetchFieldArray = (tab: tabType) => {
    switch (tab) {
      case 'client':
        return fieldArrayClient;
      case 'partner':
        return fieldArrayPartner;
      case 'joint':
        return fieldArrayJoint;
      default:
        return fieldArrayClient;
    }
  };

  const summary: ISummaryItem[] = [
    { title: 'Total Expenditure Monthly', value: calculateTotalExpenditure('Annually') / 12 },
    { title: 'Total Expenditure Annually', value: calculateTotalExpenditure('Annually') },
    { title: 'Net Disposable Income Monthly', value: totalIncomeMonthly - calculateTotalExpenditure('Annually') / 12 },
    { title: 'Net Disposable Income Annually', value: totalIncomeAnnually - calculateTotalExpenditure('Annually') },
  ];

  const tabSections: ('summary' | 'joint' | 'client' | 'partner')[] = ['summary', 'client', 'partner', 'joint'];

  const getClientsOptions = tabSections.map((type) => ({
    label: getLabel(
      { value: type, label: type.charAt(0).toUpperCase() + type.slice(1) },
      [FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from) && !isClient ? clientPartnerDetails : updatedClientPartnerDetails
    ),
    value: type,
  }));
  const onSubmit = (formValue: EVSchemaType) => {
    const formdata = createExpenditurePayload(formValue);
    const payload = { clientID: state?.clientData?.clientID, ...formdata };
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      captureExpenditure.mutateAsync(payload);
    }
    if (from === FactFindType.AI) {
      const payloadAI = {
        client_id: state.clientData.clientID,
        section: InteractionSubSectionType.expenditure_details,
        data: { response: { expenditure: formdata } },
      };
      captureExpenditureAI.mutateAsync(payloadAI);
    }
  };

  return (
    <Box>
      {isAllTranscripts.value && <AllTranscripts isAllTranscripts={isAllTranscripts} />}

      {isTranscript.value && <Transcript section="Expenditure" isTranscript={isTranscript} />}

      {!isAllTranscripts.value && !isTranscript.value && (
        <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
          <Stack direction="row" alignItems="center">
            <FactFindHeading
              sectionName="Expenditure"
              isAllTranscripts={isAllTranscripts}
              isTranscript={isTranscript}
              hideButton={sourceControlData?.dataSourceMap?.Expenditure === 'ManualInput'}
              hideAllButton={isClient || [FactFindType.AI, FactFindType.PARA_PLANNER].includes(from) || isManagedClient}
            />

            {conflictData?.length > 0 && (
              <Tooltip title="Click here to view all the conflicts">
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => {
                    setConflictsDialog(true);
                  }}
                  sx={{ marginTop: '-12px' }}
                >
                  <WarningRounded />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          {fieldErrors?.length > 0 && <ErrorSection errors={fieldErrors} />}

          <Stack sx={{ flexDirection: 'row', alignItems: 'center', mb: 2.5 }}>
            <Autocomplete
              options={
                from === FactFindType.PARA_PLANNER ? getClientsOptions.filter((e) => !['Client', 'Partner', 'Joint'].includes(e.label)) : getClientsOptions
              }
              id="expenditureDropdown"
              value={getClientsOptions.find((option) => option.value === currentTab)?.label ?? ''}
              sx={{ width: 300 }}
              onChange={(e, v) => handleChangeTab(e, v?.value)}
              getOptionLabel={(option) => option?.label || option}
              renderInput={(params) => <TextField {...params} />}
              disableClearable={true}
            />
          </Stack>

          <Stack spacing={1} marginTop={2}>
            {currentTab === 'summary' && (
              <SummaryTabComponent
                section="Expenditure"
                clientPartnerDetails={from === FactFindType.AI ? updatedClientPartnerDetails : clientPartnerDetails}
                summaryData={summaryData}
              />
            )}

            {['client', 'partner', 'joint'].map(
              (tab) =>
                currentTab === tab && (
                  <>
                    {isReadOnly && Object.keys(viewExpenditure?.data?.data?.response?.expenditure || {})?.length === 0 ? (
                      <Card>
                        <Stack direction="row" justifyContent="center" alignItems="center" height="60vh">
                          <EmptyContent description="No expenditure information has been captured as part of the fact-finding process." />
                        </Stack>
                      </Card>
                    ) : (
                      <ExpenditureTable key={tab} tab={tab} fieldArray={fetchFieldArray(tab)} isClient={isClient} isReadOnly={isReadOnly} />
                    )}
                  </>
                )
            )}
          </Stack>
          {currentTab !== 'summary' && !isReadOnly && Object.keys(viewExpenditure?.data?.data?.response?.expenditure || {})?.length === 0 && (
            <SummaryCards summary={summary} />
          )}
          {isClient ? null : (
            <>
              {[FactFindType.FACT_FIND, FactFindType.AI].includes(from) && (
                <FactFindFooter isManagedClient={isManagedClient} from={from} hideNextStep={from === FactFindType.AI} hideSave={isReadOnly} />
              )}
              <SaveDialog open={captureExpenditure.isPending} />
              <SuccessDialog open={isSuccess.value} />
              <ErrorDialog open={isError.value} />
              <ConfirmNavigationDialog open={confirmNavigationModal} handleClose={handleCloseNavigationModal} data={{ handleDiscardAndProceed, blocker }} />
              <ConflictsDialog
                open={conflictsDialog}
                handleClose={() => setConflictsDialog(false)}
                tableData={conflictData || []}
                isPending={false}
                title={'Expenditure'}
                refetch={refetch}
              />
            </>
          )}
        </FormProvider>
      )}
    </Box>
  );
};

interface ExpenditureTableProps {
  tab: tabType;
  fieldArray: UseFieldArrayReturn<EVSchemaType, tabType>;
  isClient: boolean;
  isReadOnly: boolean;
}

const ExpenditureTable = ({ tab, fieldArray, isClient, isReadOnly }: ExpenditureTableProps) => {
  const defaultEnums = useDefaultEnumsStore((state) => state.defaultEnums);
  const CategoryOptions = defaultEnums?.expenditureCategoryType?.map((e) => e.expenditureCategoryType) ?? [];
  const frequencyOptions =
    defaultEnums?.frequencyType
      ?.filter((e) => e.frequencyTypeID === '1002' || e.frequencyTypeID === '1004')
      .map((e) => {
        return { label: e.frequencyType, value: e.frequencyType };
      }) ?? [];

  const TABLE_EXPENDITURE_DETAILS = [
    { label: 'Details', name: 'detail', component: RHFTextField, minWidth: 150, width: 150, show: true },
    {
      label: 'Category',
      name: 'category',
      component: RHFAutocomplete,
      options: CategoryOptions,
      minWidth: 150,
      width: 150,
      show: true,
    },
    { label: 'Vendor Name', name: 'vendorName', component: RHFTextField, minWidth: 150, width: 150, show: true },
    {
      label: 'Amount',
      name: 'amount',
      component: RHFReactNumberFormat,
      prefix: true,
      decimalScale: 2,
      thousandSeparator: true,
      fixedDecimalScale: true,
      textAlign: 'right',
      minWidth: 150,
      width: 150,
      show: true,
    },
    { label: 'Frequency', name: 'frequency', component: RHFRadioGroup, options: frequencyOptions, row: true, minWidth: 250, width: 200, show: true },
    { label: 'Payment Method', name: 'paymentMethod', component: RHFTextField, minWidth: 150, width: 150, show: true },
    { label: 'Due Date', name: 'dueDate', component: RHFDatePicker, minWidth: 150, width: 150, show: true },
    { label: '', name: 'action', component: RHFReactNumberFormat, minWidth: 50, width: 50, show: true },
  ];

  return (
    <Fragment>
      {fieldArray.fields?.length > 0 && (
        <SimpleFieldsTable
          fieldArray={fieldArray.fields}
          currentTab={tab}
          onRemove={fieldArray.remove}
          tableDetails={TABLE_EXPENDITURE_DETAILS}
          isClient={isClient}
          isReadOnly={isReadOnly}
        />
      )}
      {isClient || isReadOnly ? null : (
        <Stack flexDirection="row" marginTop={1}>
          <Button
            variant="text"
            color="primary"
            sx={{ marginLeft: 1 }}
            size="small"
            startIcon={<PlusIcon />}
            onClick={() =>
              fieldArray.append({ detail: '', category: '', vendorName: '', amount: 0, frequency: '', paymentMethod: '', dueDate: '', show: true })
            }
          >
            Add Other Expenditure
          </Button>
        </Stack>
      )}
    </Fragment>
  );
};

const SummaryCards = ({ summary }: { summary: ISummaryItem[] }) => {
  const theme = useTheme();
  return (
    <Grid container spacing={2} mt={1} marginBottom={15}>
      {summary.map((data) => (
        <Grid
          key={data.title}
          size={{
            lg: 6,
            md: 6,
            sm: 6,
          }}
        >
          <Card sx={{ background: alpha(theme.palette.grey[100], 1) }}>
            <CardContent
              component={Stack}
              gap={2}
              flexDirection="row"
              justifyContent="space-between"
              sx={{ p: 2, pb: '8px !important', pt: '8px !important' }}
              alignItems="center"
            >
              <Typography variant="subtitle1">{data.title}</Typography>
              <Chip
                label={getCurrencySymbol('GBP') + fNumber(data.value)}
                sx={{ fontWeight: 'bolder' }}
                variant="soft"
                color={data.value >= 0 ? 'success' : 'error'}
              />
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};
